import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[2000] bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, showCloseButton = true, ...props }, ref) => {
  const [portalContainer, setPortalContainer] = React.useState(null);

  React.useEffect(() => {
    // Detect if we are in fullscreen and which element is the root
    if (typeof document !== 'undefined') {
      const resolveFullscreenPortalContainer = (el) => {
        // Keep dialogs attached to app-level fullscreen roots (e.g. PDF viewer),
        // but avoid re-parenting into media fullscreen targets (video/iframe),
        // or any element inside an active dialog/video player container,
        // which can break native fullscreen playback controls and trigger re-mount unmount loops.
        if (!el) return null;
        const tag = String(el.tagName || '').toUpperCase();
        if (tag === 'VIDEO' || tag === 'AUDIO' || tag === 'IFRAME') return null;
        if (el.closest?.('[role="dialog"]') || el.classList?.contains('video-player-container')) return null;
        return el;
      };

      const updateContainer = () => {
        setPortalContainer(
          resolveFullscreenPortalContainer(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement ||
            null
          )
        );
      };
      updateContainer();
      document.addEventListener('fullscreenchange', updateContainer);
      document.addEventListener('webkitfullscreenchange', updateContainer);
      document.addEventListener('mozfullscreenchange', updateContainer);
      document.addEventListener('MSFullscreenChange', updateContainer);
      return () => {
        document.removeEventListener('fullscreenchange', updateContainer);
        document.removeEventListener('webkitfullscreenchange', updateContainer);
        document.removeEventListener('mozfullscreenchange', updateContainer);
        document.removeEventListener('MSFullscreenChange', updateContainer);
      };
    }
  }, []);

  return (
    <DialogPortal container={portalContainer}>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "z-[2000] shadow-2xl transition-all duration-300 outline-none flex flex-col bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] text-foreground",
        "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] rounded-2xl h-auto max-h-[calc(100dvh-(env(safe-area-inset-top)+env(safe-area-inset-bottom)+9rem))] p-6 overflow-y-auto overflow-x-hidden",
        "md:max-w-lg md:w-full",
        className
      )}
      {...props}>
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
